import React, {FormEvent, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {TextField} from "@bounce/shared/consumer/bounce-ui";
import {PaymentsImages} from "@bounce/shared/consumer/components";
import {PaymentType} from "@bounce/shared/consumer/declarations";
import {
  PaymentContext,
  PaymentTermsAndConditionsV2,
  TermsAndConditionsApproval,
  TermsAndConditionsDialog,
  TermsType,
} from "@bounce/shared/consumer/pages/payment";
import {EMAIL_REGEX} from "@bounce/shared/constants";
import {PaymentCardType} from "@bounce/shared/enums";
import {Checkbox, Container, FormControlLabel, LoadingButton} from "@bounce/shared/ui";
import {PlaidPaymentMethodRead, useGetWalletByAccount} from "~/api/requests";
import {Pages} from "~/bi-events/enum";
import {useAmplitude} from "~/contexts";
import {usePartnerUi} from "~/contexts/PartnerUiContext";
import {usePlaidConnect} from "~/hooks";
import PlaidBankSection from "./PlaidBankSection";

const INVALID_EMAIL_MESSAGE = "Please enter a valid email address";

interface AchPlaidPanelProps {
  accountId: string;
  primaryEmail?: string;
  submitLabelId?: string;
  termsAndConditionsId?: string;
  onSavedMethodSubmit: (paymentMethodId: number, paymentType: PaymentType, email?: string) => void;
}

const AchPlaidPanel = ({
  accountId,
  primaryEmail,
  submitLabelId = "Set up plan",
  termsAndConditionsId = TermsType.PAYMENT,
  onSavedMethodSubmit,
}: AchPlaidPanelProps) => {
  const {pageClickEvent, pageViewEvent} = useAmplitude();
  const {partnerDetails} = usePartnerUi();
  const {buttonTouched, setButtonTouched, isLoading} = useContext(PaymentContext);

  const [email, setEmail] = useState(primaryEmail ?? "");
  const [emailTouched, setEmailTouched] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [selectedCardId, setSelectedCardId] = useState<string | number | undefined>(undefined);
  const [connecting, setConnecting] = useState(false);
  const [pendingSelectId, setPendingSelectId] = useState<number | undefined>(undefined);

  const {data: walletData, isError: walletError} = useGetWalletByAccount({accountId}, {enabled: !!accountId});

  const onConnected = useCallback(
    (paymentMethods: PlaidPaymentMethodRead[]) => {
      setConnecting(false);
      if (paymentMethods.length === 0) {
        return;
      }
      pageClickEvent({page: Pages.ACH_FORM, cta: "connect_bank_plaid"});
      setPendingSelectId(paymentMethods[0].id);
    },
    [pageClickEvent]
  );

  const {embeddedToken, loadEmbeddedToken, resetEmbeddedToken, handleSuccess, handleExit, tokenError, isConnecting} =
    usePlaidConnect({accountId, onConnected});

  const walletLoaded = walletData !== undefined || walletError;
  const achMethods = useMemo(
    () =>
      (walletData ?? []).filter(
        (method) => method.type === PaymentCardType.ACH && !method.expired && method.plaid_connected
      ),
    [walletData]
  );
  const hasMethods = achMethods.length > 0;
  const showEmbed = connecting || (walletLoaded && !hasMethods);
  const emailInvalid = !!email && !EMAIL_REGEX.test(email);
  const canFinalize = termsChecked && !!selectedCardId && !emailInvalid;

  const onAddPaymentMethod = () => {
    pageClickEvent({page: Pages.ACH_FORM, cta: "connect_bank_plaid_add"});
    resetEmbeddedToken();
    setConnecting(true);
  };

  const onCancelConnect = () => {
    setConnecting(false);
  };

  const onRetryConnect = () => {
    resetEmbeddedToken();
  };

  const finalize = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (emailInvalid) {
      setEmailTouched(true);
      return;
    }
    if (!canFinalize || buttonTouched || !selectedCardId) {
      return;
    }
    pageClickEvent({page: Pages.ACH_FORM, cta: "pay_with_saved_method"});
    setButtonTouched(true);
    onSavedMethodSubmit(Number(selectedCardId), "ACH", email || undefined);
  };

  useEffect(() => {
    if (showEmbed && !pendingSelectId) {
      loadEmbeddedToken();
    }
  }, [showEmbed, pendingSelectId, loadEmbeddedToken]);

  useEffect(() => {
    if (!hasMethods) return;
    if (pendingSelectId && achMethods.some((method) => method.id === pendingSelectId)) {
      setSelectedCardId(pendingSelectId);
      setPendingSelectId(undefined);
      return;
    }
    if (selectedCardId && achMethods.some((method) => method.id === Number(selectedCardId))) return;
    const primary = achMethods.find((method) => method.is_primary);
    setSelectedCardId(primary?.id ?? achMethods[0]?.id);
  }, [achMethods, hasMethods, selectedCardId, pendingSelectId]);

  return (
    <form noValidate onSubmit={finalize} style={{width: "100%"}}>
      <Container fullWidth sx={{alignItems: "stretch", gap: "16px"}}>
        <PlaidBankSection
          achMethods={achMethods}
          embeddedToken={embeddedToken}
          hasMethods={hasMethods}
          selectedCardId={selectedCardId}
          showEmbed={showEmbed}
          tokenError={tokenError}
          walletLoaded={walletLoaded}
          onAddPaymentMethod={onAddPaymentMethod}
          onCancelConnect={onCancelConnect}
          onRetryConnect={onRetryConnect}
          onExit={handleExit}
          onSuccess={handleSuccess}
          setSelectedCardId={setSelectedCardId}
        />

        <TextField
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={() => setEmailTouched(true)}
          label="Email address for receipts"
          type="email"
          error={emailTouched && emailInvalid}
          helperText={emailTouched && emailInvalid ? INVALID_EMAIL_MESSAGE : undefined}
          fullWidth
        />

        <PaymentsImages />

        <FormControlLabel
          control={
            <Checkbox
              checked={termsChecked}
              onChange={(_e, checked) => setTermsChecked(checked)}
              size="small"
              sx={{alignSelf: "flex-start", marginTop: "0px"}}
            />
          }
          label={
            <TermsAndConditionsApproval
              termsAndConditionsId={termsAndConditionsId}
              page={Pages.PAYMENT_SUMMARY}
              pageClickEvent={pageClickEvent}
              pageViewEvent={pageViewEvent}
              page_T_C={Pages.T_C}
              TermsAndConditionsDialog={TermsAndConditionsDialog}
              PaymentTermsAndConditionsV2={PaymentTermsAndConditionsV2}
              partnerDetails={partnerDetails}
              sx={{textAlign: "start"}}
            />
          }
          sx={{alignItems: "flex-start"}}
        />

        <LoadingButton type="submit" disabled={!canFinalize || buttonTouched} loading={isLoading || isConnecting}>
          {submitLabelId}
        </LoadingButton>
      </Container>
    </form>
  );
};

export default AchPlaidPanel;
